import { useMemo } from 'react';

import { collection } from 'firebase/firestore';

import { getLocationPosition } from '@src/components/LocationsMap';
import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import { useFirestoreQuery } from '@src/packages/utils';
import { getMatchingTriggerProgressLabel } from '@src/packages/utils/functions/triggers';

import type {
  TimelineEventData,
  TimelineEventTrigger,
  UseTimelineEventReturn,
} from '../TimelineEvent.types';
import {
  formatEventDateTime,
  getActorName,
  getContextSegments,
  getEventIconColor,
  getTargetName,
  isCheckpointRouteEvent,
} from '../utils/mapTimelineEventPresentation';

// TODO(services): Re-enable when execution trigger progress is updated reliably.
const ENABLE_TRIGGER_PROGRESS_LABEL = false;

export const useTimelineEvent = (event: TimelineEventData): UseTimelineEventReturn => {
  const locationMarked = event.locations?.[0];
  const { execution, division, organization } = event;
  const isCheckpointRoute = isCheckpointRouteEvent(event);

  const orgId = organization?.id ?? execution?.organization?.id;
  const divId = division?.id ?? execution?.division?.id;
  const executionId = execution?.id;

  // Live counters live on executions/{id}/triggers — not on denormalized event.trigger.
  // Keep the subscription off while the label is hidden: one listener per visible card.
  const canFetchTriggers =
    ENABLE_TRIGGER_PROGRESS_LABEL && Boolean(executionId && orgId && divId);

  const triggersQuery = useMemo(() => {
    if (!canFetchTriggers || !orgId || !divId || !executionId) {
      return false;
    }
    return collection(
      db,
      FSCollections.ORGANIZATIONS,
      orgId,
      FSCollections.DIVISIONS,
      divId,
      FSCollections.EXECUTIONS,
      executionId,
      FSCollections.TRIGGERS,
    );
  }, [canFetchTriggers, divId, executionId, orgId]);

  const { data: fetchedTriggers, loading: triggerLoading } = useFirestoreQuery(triggersQuery, [
    orgId,
    divId,
    executionId,
    canFetchTriggers,
  ]);

  const executionTriggers = useMemo(() => {
    if (!Array.isArray(fetchedTriggers)) {
      return null;
    }
    return fetchedTriggers as TimelineEventTrigger[];
  }, [fetchedTriggers]);

  const iconColor = getEventIconColor(event, isCheckpointRoute);
  const actorName = getActorName(event);
  const targetName = getTargetName(event, isCheckpointRoute);
  const markerLocationName =
    !isCheckpointRoute && locationMarked?.name?.trim() ? locationMarked.name.trim() : null;
  const contextSegments = getContextSegments(event, isCheckpointRoute, event.trigger);
  const progressLabel = ENABLE_TRIGGER_PROGRESS_LABEL
    ? getMatchingTriggerProgressLabel(executionTriggers, {
        triggerId: event.triggerId,
        fallback: event.trigger,
      })
    : null;
  const dateTimeLabel = formatEventDateTime(event.createdAtDevice);

  const checkpointRouteMapItem = useMemo(() => {
    if (!isCheckpointRoute || !locationMarked) {
      return null;
    }

    const position = getLocationPosition(locationMarked);
    if (!position) {
      return null;
    }

    return {
      id: locationMarked.id ?? event.id,
      name: locationMarked.name,
      position,
    };
  }, [event.id, isCheckpointRoute, locationMarked]);

  return {
    isCheckpointRoute,
    iconColor,
    actorName,
    targetName,
    markerLocationName,
    contextSegments,
    progressLabel,
    dateTimeLabel,
    triggerLoading: Boolean(canFetchTriggers && triggerLoading),
    eventIcon: event.eventType?.icon,
    checkpointRouteMapItem,
  };
};
